import React, { createContext, useState, useEffect, useCallback } from "react";
import {
  UserEntity,
  CompanyEntity,
  LoginCredentials,
  LoginUseCase,
  AdminLoginUseCase,
  LogoutUseCase,
  UpdateZohoTokenUseCase,
  GetCurrentUserUseCase,
} from "../../domain";
import { AuthRepositoryImpl } from "../../data/repositories/AuthRepositoryImpl";

/**
 * Auth Context Type
 * Defines the shape of the authentication context
 */
export interface AuthContextType {
  user: UserEntity | null;
  company: CompanyEntity | null;
  isAuthenticated: boolean;
  loading: boolean; // For initial auth check
  loginLoading: boolean; // For login action
  error: string | null;
  loginError: string | null; // Separate error for login
  login: (
    credentials: LoginCredentials,
  ) => Promise<{ success: boolean; needsZohoAuth?: boolean }>;
  adminLogin: (credentials: LoginCredentials) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  updateZohoToken: (refreshToken: string) => Promise<{ success: boolean }>;
  needsZohoAuth: () => boolean;
  isAdmin: () => boolean;
  /** True when role is Admin or CFO (admin dashboard entry). */
  isAdminOrCfo: () => boolean;
  isClientView: () => boolean;
  hasPermission: (permission: string) => boolean;
  clearError: () => void;
  clearLoginError: () => void;
  /** Re-read auth from storage and update context state (e.g. after FromMasterPage stores token/user). */
  refreshAuthFromStorage: () => void;
}

// Create repository instance (singleton)
const authRepository = new AuthRepositoryImpl();

// Create use cases
const loginUseCase = new LoginUseCase(authRepository);
const adminLoginUseCase = new AdminLoginUseCase(authRepository);
const logoutUseCase = new LogoutUseCase(authRepository);
const updateZohoTokenUseCase = new UpdateZohoTokenUseCase(authRepository);
const getCurrentUserUseCase = new GetCurrentUserUseCase(authRepository);

// Create the context
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth Provider Component
 * Provides authentication state and operations to the entire app
 * This ensures all components share the same auth state
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserEntity | null>(null);
  const [company, setCompany] = useState<CompanyEntity | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const storedUser = authRepository.getStoredUser();
        const storedCompany = authRepository.getStoredCompany();
        const authenticated = authRepository.isAuthenticated();
        if (authenticated && storedUser) {
          setUser(storedUser);
          setCompany(storedCompany);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        // Clear invalid data
        authRepository.logout();
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  /**
   * Login
   */
  const login = useCallback(
    async (
      credentials: LoginCredentials,
    ): Promise<{ success: boolean; needsZohoAuth?: boolean }> => {
      setLoginLoading(true);
      setLoginError(null);
      setError(null);

      try {
        const result = await loginUseCase.execute(credentials);

        setUser(result.user);
        setCompany(result.company);
        setIsAuthenticated(true);

        return {
          success: true,
          needsZohoAuth: result.needsZohoAuth,
        };
      } catch (error: any) {
        const errorMessage = error.message || "Login failed. Please try again.";
        setLoginError(errorMessage);
        return { success: false };
      } finally {
        setLoginLoading(false);
      }
    },
    [],
  );

  /**
   * Admin Login
   */
  const adminLogin = useCallback(
    async (credentials: LoginCredentials): Promise<{ success: boolean }> => {
      setLoginLoading(true);
      setLoginError(null);
      setError(null);

      try {
        const result = await adminLoginUseCase.execute(credentials);

        setUser(result.user);
        setCompany(result.company);
        setIsAuthenticated(true);

        return { success: true };
      } catch (error: any) {
        const errorMessage =
          error.message || "Admin login failed. Please try again.";
        setLoginError(errorMessage);
        return { success: false };
      } finally {
        setLoginLoading(false);
      }
    },
    [],
  );

  /**
   * Logout
   */
  const logout = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      await logoutUseCase.execute();
    } catch (error) {
      console.warn("Logout error:", error);
    } finally {
      // Get user role and master admin info before clearing data
      const currentUser = user;
      const userRole = currentUser?.role?.toLowerCase();
      const masterAdminUrl = localStorage.getItem("master_admin_url");
      const cameFromMasterAdmin = localStorage.getItem(
        "came_from_master_admin",
      );

      setUser(null);
      setCompany(null);
      setIsAuthenticated(false);
      setError(null);
      setLoading(false);

      // Determine redirect based on user origin and role
      if (
        cameFromMasterAdmin === "true" &&
        masterAdminUrl &&
        userRole !== "client_view"
      ) {
        // User came from master admin and is not client_view - redirect to master admin
        localStorage.removeItem("came_from_master_admin");
        localStorage.removeItem("master_admin_url");
        // window.location.href = masterAdminUrl;
        window.location.replace(masterAdminUrl);
      } else {
        // client_view role or normal tenant user - redirect to tenant login page
        localStorage.removeItem("came_from_master_admin");
        localStorage.removeItem("master_admin_url");
        // window.location.href = "/";
        window.location.replace("/");
      }
    }
  }, [user]);

  /**
   * Update Zoho Token
   */
  const updateZohoToken = useCallback(
    async (refreshToken: string): Promise<{ success: boolean }> => {
      setLoading(true);
      setError(null);

      try {
        await updateZohoTokenUseCase.execute(refreshToken);

        // Update user state with new token
        if (user) {
          const updatedUser = { ...user, zohoRefreshToken: refreshToken };
          setUser(updatedUser);
        }

        return { success: true };
      } catch (error: any) {
        const errorMessage = error.message || "Failed to update Zoho token.";
        setError(errorMessage);
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [user],
  );

  /**
   * Check if user needs Zoho authentication
   */
  const needsZohoAuth = useCallback((): boolean => {
    return getCurrentUserUseCase.needsZohoAuth();
  }, []);

  /**
   * Check if user is admin
   */
  const isAdmin = useCallback((): boolean => {
    return getCurrentUserUseCase.isAdmin();
  }, []);

  const isAdminOrCfo = useCallback((): boolean => {
    return getCurrentUserUseCase.isAdminOrCfo();
  }, []);

  /**
   * Check if user has permission
   */
  const hasPermission = useCallback((permission: string): boolean => {
    return getCurrentUserUseCase.hasPermission(permission);
  }, []);

  /**
   * Check if user has client_view role
   */
  const isClientView = useCallback((): boolean => {
    return user?.role?.toLowerCase() === "client_view";
  }, [user]);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  /**
   * Clear login error
   */
  const clearLoginError = useCallback((): void => {
    setLoginError(null);
  }, []);

  /**
   * Re-read auth from storage and update context state.
   * Call after storing token/user/company (e.g. on FromMasterPage) so ProtectedRoute sees the user.
   */
  const refreshAuthFromStorage = useCallback((): void => {
    try {
      const storedUser = authRepository.getStoredUser();
      const storedCompany = authRepository.getStoredCompany();
      const authenticated = authRepository.isAuthenticated();
      if (authenticated && storedUser) {
        setUser(storedUser);
        setCompany(storedCompany);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setCompany(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error("Refresh auth from storage failed:", err);
      setUser(null);
      setCompany(null);
      setIsAuthenticated(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        company,
        isAuthenticated,
        loading,
        loginLoading,
        error,
        loginError,
        login,
        adminLogin,
        logout,
        updateZohoToken,
        needsZohoAuth,
        isAdmin,
        isAdminOrCfo,
        isClientView,
        hasPermission,
        clearError,
        clearLoginError,
        refreshAuthFromStorage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
